import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import ContentBlock from '~/components/Layout/Builders/ContentBlock';
import { ArticleContext } from '~/contexts/ArticleContext';
import { AppDataContext } from '~/contexts/AppDataContext';
import { maybeGetSlide } from '~/utilities/maybeGetSlide';
import type { ArticleInterface } from '~/types/AppData/PageContent/ArticleInterface';
import { maybeMoveVideoPlayer } from '~/utilities/maybeMoveVideoPlayer';
import { useIsOdesaTemplate } from '~/utilities/useIsOdesaTemplate';
import { getKeyFromBlock } from '~/utilities/getKeyFromBlock';

const contentClasses = cva('article-content', {
  variants: {
    isOdesa: {
      true: '[&_p]:leading-[30px] [&_p]:md:leading-[28px] [&_li]:leading-[30px] [&_li]:md:leading-[28px]',
      false: '',
    },
  },
});

const STORY_CARDS_EMBED = `<div id="story-minutemedia/8zFpLWaC3"></div><script src="https://story.si.com/8zFpLWaC3/sdk-prod.js?ver=4dc1534e-5dec-4623-8f75-4308a4bb03cc-1" onload="(() => embedStory('minutemedia/8zFpLWaC3', {autoScrollOffset:[['min-width: 768px',80],['max-width: 767px',120]],domain:'https://story.si.com/8zFpLWaC3/'}))()" data-id="minutemedia/8zFpLWaC3" async></script>`;
const PROPERTY_NAME = 'sports_illustrated_ftbol';
const PROPERTY_URL = 'es-us/futbol/';
const TEMPLATE_NAME = 'PostPageFiji';
const STORY_CARDS_POLL = 'story-cards-poll';
const PLACEMENT_MIDDLE = `${STORY_CARDS_POLL}|poll-middle`;
const PLACEMENT_END = `${STORY_CARDS_POLL}|poll-end`;
const PLACEMENT_CONTROL = `${STORY_CARDS_POLL}|control`;

export default component$((): JSX.Element => {
  const { payloadToSSR, appState, query, env } = useContext(AppDataContext);
  const article = useContext(ArticleContext);
  const { experiments, edition, isMobileViewer } = payloadToSSR;
  const isOdesa = useIsOdesaTemplate();

  if (!article) return <></>;

  const slideshow = payloadToSSR.pageContent.slideshow ?? article.slideshow;

  // Get the content items to calculate middle position
  const contentItems = maybeMoveVideoPlayer(
    maybeGetSlide(
      article as ArticleInterface,
      slideshow,
      appState.currentSlide,
      experiments
    ),
    edition,
    !!isMobileViewer
  );

  const middleIndex = Math.floor(contentItems.length / 2);

  // TODO: Remove the story cards logic once the POC is done
  const shouldShowStoryCards =
    payloadToSSR.propertySettings.property === PROPERTY_NAME &&
    payloadToSSR.canonicalURL.includes(PROPERTY_URL) &&
    payloadToSSR.template === TEMPLATE_NAME;

  //Use query params in dev/QA, experiments array in production
  const isProduction = env === 'production';
  const experimentsArray = experiments || [];

  let showPollMiddle = false;
  let showPollEnd = false;

  if (isProduction) {
    showPollMiddle =
      shouldShowStoryCards && experimentsArray.includes(PLACEMENT_MIDDLE);
    showPollEnd =
      shouldShowStoryCards && experimentsArray.includes(PLACEMENT_END);
  } else {
    const pollPlacement = query?.['mm-experiments'];
    const isNotControlGroup = query?.['mm-experiments'] !== PLACEMENT_CONTROL;

    showPollMiddle =
      shouldShowStoryCards &&
      isNotControlGroup &&
      pollPlacement === PLACEMENT_MIDDLE;
    showPollEnd =
      shouldShowStoryCards &&
      isNotControlGroup &&
      pollPlacement === PLACEMENT_END;
  }

  return (
    <div class={contentClasses({ isOdesa })}>
      {contentItems.map((each, index) => {
        const shouldShowPollAfterThis = showPollMiddle && index === middleIndex;

        return (
          <>
            <ContentBlock
              key={getKeyFromBlock(each, index)}
              item={each}
              index={index}
            />
            {shouldShowPollAfterThis && (
              <div
                style={{ margin: '20px 0' }}
                dangerouslySetInnerHTML={STORY_CARDS_EMBED}
              />
            )}
          </>
        );
      })}

      {showPollEnd && (
        <div
          style={{ margin: '20px 0' }}
          dangerouslySetInnerHTML={STORY_CARDS_EMBED}
        />
      )}
    </div>
  );
});
